import type { ComponentProps, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Clock, Mail, MapPin, Phone } from 'lucide-react';

import { Logo, cn } from '@ibas/ui';
import { NavyAtmosphere, ScrollReveal } from '@/components/motion';
import { brand } from '@/config/brand';
import { primaryNav } from '@/config/site';
import { CookieBanner } from './cookie-banner';
import {
  CONTACT_ADDRESS,
  CONTACT_EMAILS,
  CONTACT_PHONE,
  CONTACT_PHONE_HOURS,
} from '@/data/about';

/**
 * SiteFooter — the shared institutional footer. Sits on the brand navy field
 * (with the house NavyAtmosphere backdrop) and carries five things the visitor
 * expects at the foot of every page: the IBAS mark + a short who-we-are line,
 * an Explore column linking every top-level page, a Programmes column, verified
 * contact details, and the social channels.
 *
 * Brand compliance: the logo artwork must sit on a white/light field (never
 * recoloured for a dark background), so it rests on a white plate here. Every
 * fact — address, phone, hours, emails, social URLs — is sourced from the
 * branding data (src/data/about.ts) and the brand config; nothing is invented.
 * The only gold control is the Apply CTA (gold bg + navy text — the permitted
 * gold-button pairing).
 */

interface FooterLink {
  label: string;
  href: string;
}

/** Explore = every top-level page, plus the application entry point. */
const EXPLORE_LINKS: FooterLink[] = [...primaryNav, { label: 'Apply', href: '/apply' }];

/** Legal — only the legal pages that exist are linked (no dead links). */
const LEGAL_LINKS: FooterLink[] = [
  { label: 'Terms & Conditions', href: '/legal/terms' },
  { label: 'Complaints', href: '/legal/complaints' },
  { label: 'Cookies', href: '/legal/cookies' },
];

/** Programmes — mirrors the mega-nav programme routes. */
const PROGRAMME_LINKS: FooterLink[] = [
  { label: 'Flexi-MBA', href: '/programmes/flexi-mba' },
  { label: 'Flexi-DBA', href: '/programmes/flexi-dba' },
  { label: 'Diploma Programme', href: '/programmes/diploma' },
  { label: 'Bachelor of Business Administration', href: '/programmes/bba' },
  { label: "Master's Programmes", href: '/programmes/masters' },
  { label: 'Introduction to online learning', href: '/programmes/online-learning' },
];

/**
 * Brand glyphs. lucide dropped its trademarked social icons, so the official
 * channel marks are inlined here as simple monochrome paths (they inherit
 * `currentColor` and take the same className as a lucide icon).
 */
type GlyphProps = ComponentProps<'svg'>;

function LinkedInGlyph(props: GlyphProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function FacebookGlyph(props: GlyphProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function InstagramGlyph(props: GlyphProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

function YouTubeGlyph(props: GlyphProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

interface SocialLink {
  label: string;
  href: string;
  Glyph: (props: GlyphProps) => ReactNode;
}

/** Official IBAS channels (URLs verified in src/data/about.ts). */
const SOCIAL_LINKS: SocialLink[] = [
  {
    label: 'LinkedIn',
    href: 'https://linkedin.com/school/international-business-academy-of-switzerland-gmbh',
    Glyph: LinkedInGlyph,
  },
  { label: 'Facebook', href: 'https://facebook.com/IBAS.Education/', Glyph: FacebookGlyph },
  { label: 'Instagram', href: 'https://instagram.com/ibas.mgwegroup', Glyph: InstagramGlyph },
  {
    label: 'YouTube',
    href: 'https://youtube.com/channel/UC_C7uAOAH9RMzZs-CKCZ62w',
    Glyph: YouTubeGlyph,
  },
];

const primaryEmail = CONTACT_EMAILS[0].email;

function FooterColumn({ heading, links }: { heading: string; links: FooterLink[] }) {
  return (
    <div>
      <h3 className="font-label text-xs font-semibold tracking-[0.18em] text-brand-gold-light uppercase">
        {heading}
      </h3>
      <ul className="mt-5 space-y-3">
        {links.map((link) => (
          <li key={link.href}>
            <Link
              to={link.href}
              className="inline-block text-sm text-white/70 transition-colors hover:text-white focus-visible:text-white focus-visible:ring-2 focus-visible:ring-brand-gold-light focus-visible:ring-offset-2 focus-visible:ring-offset-brand-navy focus-visible:outline-none"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SiteFooter() {
  return (
    <>
      {/*
       * The cookie notice rides with the footer because the footer is the one
       * shared element on EVERY page — the bespoke home composition carries its
       * own shell (it does not render SiteLayout), so mounting the banner here
       * is the single place that reaches the whole site exactly once. The
       * banner itself is position:fixed, so its DOM location is irrelevant to
       * where it appears.
       */}
      <CookieBanner />
      <footer className="relative isolate overflow-hidden bg-brand-navy text-white">
        <NavyAtmosphere />

        <div className="relative z-10 mx-auto max-w-7xl px-6 py-16 lg:px-8 lg:py-20">
          <ScrollReveal
            as="div"
            from="up"
            stagger={0.08}
            className="grid grid-cols-2 gap-x-8 gap-y-12 lg:grid-cols-12"
          >
            {/* Brand — logo, description, social, CTA */}
            <div className="col-span-2 lg:col-span-4">
              {/* Logo rests on a white plate (brand rule: never on a dark field). */}
              <Link
                to="/"
                aria-label="IBAS home"
                className="inline-flex rounded-xl bg-white px-4 py-3 shadow-sm transition-transform hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-brand-gold-light focus-visible:ring-offset-2 focus-visible:ring-offset-brand-navy focus-visible:outline-none"
              >
                <Logo className="w-[150px]" />
              </Link>

              <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/70">
                International Business Academy of Switzerland (IBAS) — accessible Swiss-quality
                business education. Study flexibly, develop practical capability, and progress
                globally.
              </p>

              {/* Social channels */}
              <ul className="mt-7 flex items-center gap-3">
                {SOCIAL_LINKS.map(({ label, href, Glyph }) => (
                  <li key={label}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={`IBAS on ${label}`}
                      className="flex size-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/80 transition-all hover:-translate-y-0.5 hover:border-brand-gold-light/40 hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-brand-gold-light focus-visible:ring-offset-2 focus-visible:ring-offset-brand-navy focus-visible:outline-none motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                    >
                      <Glyph className="size-[18px]" />
                    </a>
                  </li>
                ))}
              </ul>

              <Link
                to="/apply"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-gold px-5 py-2.5 font-label text-sm font-semibold text-brand-navy transition-all hover:-translate-y-0.5 hover:bg-brand-gold-light focus-visible:ring-2 focus-visible:ring-brand-gold-light focus-visible:ring-offset-2 focus-visible:ring-offset-brand-navy focus-visible:outline-none motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                Start your application
                <ArrowUpRight className="size-4" aria-hidden />
              </Link>
            </div>

            {/* Explore */}
            <div className="lg:col-span-2">
              <FooterColumn heading="Explore" links={EXPLORE_LINKS} />
            </div>

            {/* Programmes */}
            <div className="lg:col-span-3">
              <FooterColumn heading="Programmes" links={PROGRAMME_LINKS} />
            </div>

            {/* Contact */}
            <div className="col-span-2 lg:col-span-3">
              <h3 className="font-label text-xs font-semibold tracking-[0.18em] text-brand-gold-light uppercase">
                Contact
              </h3>
              <ul className="mt-5 space-y-4 text-sm text-white/70">
                <li className="flex gap-3">
                  <MapPin aria-hidden className="mt-0.5 size-4 shrink-0 text-brand-gold-light" />
                  <span className="not-italic">{CONTACT_ADDRESS}</span>
                </li>
                <li className="flex gap-3">
                  <Phone aria-hidden className="mt-0.5 size-4 shrink-0 text-brand-gold-light" />
                  <a
                    href={`tel:${CONTACT_PHONE.replace(/\s+/g, '')}`}
                    className="transition-colors hover:text-white focus-visible:text-white focus-visible:outline-none"
                  >
                    {CONTACT_PHONE}
                  </a>
                </li>
                <li className="flex gap-3">
                  <Clock aria-hidden className="mt-0.5 size-4 shrink-0 text-brand-gold-light" />
                  <span>{CONTACT_PHONE_HOURS}</span>
                </li>
                <li className="flex gap-3">
                  <Mail aria-hidden className="mt-0.5 size-4 shrink-0 text-brand-gold-light" />
                  <a
                    href={`mailto:${primaryEmail}`}
                    className="transition-colors hover:text-white focus-visible:text-white focus-visible:outline-none"
                  >
                    {primaryEmail}
                  </a>
                </li>
              </ul>
            </div>
          </ScrollReveal>

          {/* Bottom bar */}
          <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-8 text-xs text-white/55 sm:flex-row sm:items-center sm:justify-between">
            <p>
              © {brand.established}–{new Date().getFullYear()} {brand.legalName}. All rights reserved.
            </p>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              {/* Legal links — only pages that exist are linked (no dead links). */}
              <nav aria-label="Legal" className="flex flex-wrap items-center gap-x-4 gap-y-2">
                {LEGAL_LINKS.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="transition-colors hover:text-white focus-visible:text-white focus-visible:ring-2 focus-visible:ring-brand-gold-light focus-visible:ring-offset-2 focus-visible:ring-offset-brand-navy focus-visible:outline-none"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
              <p className={cn('flex items-center gap-2')}>
                <span>Thalwil, Switzerland</span>
                <span aria-hidden className="text-white/25">
                  •
                </span>
                <span>{brand.institutionalTagline}</span>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
